import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';

import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Button, Chip, IconButton, TextField, Typography, Select, MenuItem, FormControl, InputAdornment } from '@mui/material';
import { AddShoppingCart, Block, CheckCircleOutline, Delete, Description, Discount, NorthEast, Search, WarningAmber } from '@mui/icons-material';
import CleanningButton from '../components/CleanningButton';
import { useEffect } from 'react';
import { useState } from 'react';

// Carga de formatos desde catálogo de invoice
import API from '../services/api';
import { tokenizeSearchText, productMatchesSearchTokens } from '../services/productSearch';
import {
    buildReconciliationDiagnostics,
    getReconciliationStatusItems,
    reconcileInvoiceDraft,
} from './InvoiceStepper/invoiceReconciliation';


export default function CustomTableInvoice({
    setOpen,
    invoice,
    setInvoice,
    products,
    setProducts,
    setCurrentDetailIndex,
    setOpenPromotionModal,
    candidateOrders = [],
    initialSearchTerm,
    onSearchTermChange,
}) {
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
    const [debouncedSearch, setDebouncedSearch] = useState(initialSearchTerm || '');
    const [formats, setFormats] = useState([]);
    const handleOpen = () => {
        setOpen(true);
    };
    // Fetch formatos una sola vez desde bulk invoice catalogs
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await API.get('invoice/catalogs/create_invoice/');
                if (mounted) setFormats(Array.isArray(data?.formats) ? data.formats : []);
            } catch {
                if (mounted) setFormats([]);
            }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        setSearchTerm(initialSearchTerm || '');
        setDebouncedSearch(initialSearchTerm || '');
    }, [initialSearchTerm]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 250);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const sumDetailsTotal = (details) => {
        const totalCents = (details || []).reduce((sum, item) => {
            const qty = Number(item?.quantity) || 0;
            const priceNum = Number(item?.price ?? item?.finalPrice);
            const priceCents = Number.isFinite(priceNum) ? Math.round(priceNum * 100) : 0;
            return sum + qty * priceCents;
        }, 0);
        return (totalCents / 100).toFixed(2);
    };
    const getPriceTypeLabel = (value) => {
        if (value == null) return '';
        if (typeof value === 'object') {
            return value.name || value.label || value.code || '';
        }
        return String(value);
    };

    const getDetailRowId = React.useCallback((row) => {
        if (row?.clientRowId) return String(row.clientRowId);
        if (row?.id != null) return `id-${row.id}`;
        const productId = row?.product_id ?? row?.product?.id ?? row?.code ?? 'x';
        const formatId = row?.prformat_id ?? row?.prformat?.id ?? row?.prformat ?? 'x';
        const dueDate = row?.dueDate ?? 'x';
        const price = row?.price ?? row?.finalPrice ?? 'x';
        return `tmp-${productId}-${formatId}-${dueDate}-${price}`;
    }, []);

    const filteredRows = React.useMemo(() => {
        const tokens = tokenizeSearchText(debouncedSearch);
        const baseRows = (invoice?.invoice_detail || []).map((row, index) => ({ row, index }));
        if (tokens.length === 0) return baseRows;
        return baseRows.filter(({ row }) => productMatchesSearchTokens(row, tokens));
    }, [debouncedSearch, invoice?.invoice_detail]);

    React.useEffect(() => {
        const seen = new Set();
        const duplicates = new Set();
        (invoice?.invoice_detail || []).forEach((row) => {
            const lineId = row?.id ?? row?.client_line_id ?? row?.clientRowId;
            if (!lineId) return;
            const key = String(lineId);
            if (seen.has(key)) duplicates.add(key);
            seen.add(key);
        });
        if (duplicates.size > 0) {
            console.warn('Invoice detail duplicated line ids', Array.from(duplicates));
        }
    }, [invoice?.invoice_detail]);

    const reconciliationView = React.useMemo(() => {
        return reconcileInvoiceDraft(invoice?.invoice_detail || [], invoice?.invoice_bonus || [], candidateOrders || []);
    }, [candidateOrders, invoice?.invoice_bonus, invoice?.invoice_detail]);

    const detailMetaByIndex = React.useMemo(() => {
        const reconciledRows = reconciliationView?.details || [];
        return reconciledRows.map((row) => row?.reconciliation || null);
    }, [reconciliationView?.details]);

    const bonusMetaByIndex = React.useMemo(() => {
        const rows = reconciliationView?.bonuses || [];
        return rows.map((row) => row?.reconciliation || null);
    }, [reconciliationView?.bonuses]);

    React.useEffect(() => {
        const debugEnabled = process.env.NODE_ENV === 'development'
            || process.env.REACT_APP_INVOICE_RECONCILIATION_DEBUG === 'true';
        if (!debugEnabled) return;

        buildReconciliationDiagnostics(
            invoice?.invoice_detail || [],
            candidateOrders || [],
            reconciliationView?.details || [],
        ).forEach((diagnostic, index) => {
            console.debug(`[invoice-reconciliation] line ${index + 1}`, diagnostic);
        });
    }, [candidateOrders, invoice?.invoice_detail, reconciliationView?.details]);

    // const renderStatusChips = React.useCallback((flags, reconciliationMeta = null) => {
    //     const statusChips = getReconciliationStatusItems(flags, reconciliationMeta).map((item) => {
    //         return (
    //             <Chip
    //                 key={item.flag}
    //                 size="small"
    //                 label={item.label}
    //                 color={item.color}
    //                 variant="outlined"
    //             />
    //         );
    //     });

    //     const allocations = Array.isArray(reconciliationMeta?.allocations)
    //         ? reconciliationMeta.allocations
    //         : [];
    //     const orderCount = new Set(allocations.map((item) => item?.order_id).filter(Boolean)).size;
    //     const allocatedQuantity = Number(reconciliationMeta?.allocated_quantity || 0);
    //     const metadataChips = [];

    //     if (orderCount > 0) {
    //         metadataChips.push(
    //             <Chip key="assigned-orders" size="small" label={`${orderCount} OC`} variant="outlined" />
    //         );
    //     }
    //     if (allocatedQuantity > 0) {
    //         metadataChips.push(
    //             <Chip key="allocated-quantity" size="small" label={`${allocatedQuantity} conciliada`} variant="outlined" />
    //         );
    //     }

    //     return [...statusChips, ...metadataChips];
    // }, []);

const renderStatusChips = React.useCallback((flags, reconciliationMeta = null) => {
    const statusItems = getReconciliationStatusItems(flags, reconciliationMeta);

    const allocations = Array.isArray(reconciliationMeta?.allocations)
        ? reconciliationMeta.allocations
        : [];
    const orderCount = new Set(allocations.map((item) => item?.order_id).filter(Boolean)).size;
    const allocatedQuantity = Number(reconciliationMeta?.allocated_quantity || 0);

    const metaItems = [];
    if (orderCount > 0) {
        metaItems.push({ flag: 'OC_COUNT', label: `${orderCount} OC`, icon: <Description sx={{ fontSize: 11 }} /> });
    }
    if (allocatedQuantity > 0) {
        metaItems.push({ flag: 'ALLOC_QTY', label: `${allocatedQuantity} conc.`, icon: <Description sx={{ fontSize: 11 }} /> });
    }

    const colorMap = {
        success: { bg: '#E1F5EE', color: '#0F6E56' },
        warning: { bg: '#FAEEDA', color: '#854F0B' },
        error:   { bg: '#FCEBEB', color: '#A32D2D' },
        info:    { bg: '#E6F1FB', color: '#185FA5' },
        default: { bg: '#F1EFE8', color: '#5F5E5A' },
    };

    const iconMap = {
        success: <CheckCircleOutline sx={{ fontSize: 11 }} />,
        warning: <WarningAmber sx={{ fontSize: 11 }} />,
        error:   <NorthEast sx={{ fontSize: 11 }} />,
        info:    <Description sx={{ fontSize: 11 }} />,
        default: <Block sx={{ fontSize: 11 }} />,
    };

    const allItems = [
        ...statusItems.map(item => ({
            key: item.flag,
            label: item.label,
            muiColor: item.color,
        })),
        ...metaItems.map(item => ({
            key: item.flag,
            label: item.label,
            muiColor: 'info',
        })),
    ];

    // Si hay OC count y allocated en statusItems, los fusionamos en uno solo
    const ocItem = allItems.find(i => i.key === 'OC_COUNT');
    const allocItem = allItems.find(i => i.key === 'ALLOC_QTY');
    const rest = allItems.filter(i => i.key !== 'OC_COUNT' && i.key !== 'ALLOC_QTY');

    const merged = [...rest];
    if (ocItem || allocItem) {
        const label = [ocItem?.label, allocItem?.label].filter(Boolean).join(' · ');
        merged.push({ key: 'oc-summary', label, muiColor: 'info' });
    }

    return merged.map(({ key, label, muiColor }) => {
        const { bg, color } = colorMap[muiColor] || colorMap.default;
        const icon = iconMap[muiColor] || iconMap.default;
        return (
            <Box
                key={key}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    px: '8px',
                    py: '2px',
                    borderRadius: '20px',
                    backgroundColor: bg,
                    color: color,
                    fontSize: '11px',
                    fontWeight: 500,
                    lineHeight: 1.6,
                    whiteSpace: 'nowrap',
                }}
            >
                {icon}
                {label}
            </Box>
        );
    });
}, []);
    const handleQuantityChange = (index, value) => {
        const safe = String(value ?? '').replace(/[^\d.]/g, ''); // solo dígitos y punto
        const updatedDetails = [...(invoice.invoice_detail || [])];
        const current = { ...(updatedDetails[index] || {}) };

        current.quantity = safe;                       // guardo como string editable
        current.quantityRe = safe === '' ? '' : Number(safe);

        updatedDetails[index] = current;
        const totalFormatted = sumDetailsTotal(updatedDetails);
        setInvoice(prev => ({ ...prev, invoice_detail: updatedDetails, total: totalFormatted }));

        
    };
    const handleBonusQuantityChange = (bonusIndex, value) => {
        setInvoice(prev => {
            const numericValue = value === '' ? 0 : Math.max(0, Number(value));

            const updatedBonuses = prev.invoice_bonus.map((bonus, idx) =>
                idx === bonusIndex
                    ? { ...bonus, quantity: numericValue, quantityRe: numericValue }
                    : bonus
            );

            return { ...prev, invoice_bonus: updatedBonuses };
        });
    };


    const handleFormatChange = (index, id) => {
        const updatedDetails = [...(invoice.invoice_detail || [])];
        const current = { ...(updatedDetails[index] || {}) };
        const normalizedId = id === '' ? null : Number(id);
        const selected = formats.find(f => String(f.id) === String(normalizedId)) || null;
        current.prformat_id = normalizedId;
        current.format_id = normalizedId;
        current.format_name = normalizedId == null ? null : (selected?.name ?? current.format_name ?? null);
        current.prformat = normalizedId == null ? null : (selected || current.prformat || null);
        current.order_detail_id = null;
        current.allocations = [];
        current.source_orders = [];
        delete current.reconciliation;
        updatedDetails[index] = current;
        setInvoice(prev => ({ ...prev, invoice_detail: updatedDetails }));
    };
    const handleBonusFormatChange = (bonusIndex, value) => {
        setInvoice(prev => {
            const updatedBonuses = [...(prev.invoice_bonus || [])];
            const current = { ...(updatedBonuses[bonusIndex] || {}) };
            const normalizedId = value === '' ? null : Number(value);
            const selected = formats.find(f => String(f.id) === String(normalizedId)) || null;
            current.prformat_id = normalizedId;
            current.format_id = normalizedId;
            current.format_name = normalizedId == null ? null : (selected?.name ?? current.format_name ?? null);
            current.prformat = normalizedId == null ? null : (selected || current.prformat || null);
            current.order_bonus_id = null;
            current.allocations = [];
            current.source_orders = [];
            delete current.reconciliation;
            updatedBonuses[bonusIndex] = current;
            return { ...prev, invoice_bonus: updatedBonuses };
        });
    };


    const handlePriceChange = (index, value) => {
        const regex = /^\d*\.?\d{0,2}$/;   // ← CORRECTO
        if (value !== '' && !regex.test(value)) return;

        const updatedDetails = [...(invoice.invoice_detail || [])];
        const current = { ...(updatedDetails[index] || {}) };
        const parsedValue = Number(value);

        // guardamos como string durante la edición
        current.price = value === '' ? '' : value;
        current.finalPrice = Number.isFinite(parsedValue) ? parsedValue : 0;

        updatedDetails[index] = current;
        const totalFormatted = sumDetailsTotal(updatedDetails);
        setInvoice(prev => ({ ...prev, invoice_detail: updatedDetails, total: totalFormatted }));
    };
    const handleSelectPrice = (index, val) => {
        const updatedDetails = [...(invoice.invoice_detail || [])];
        const current = { ...(updatedDetails[index] || {}) };

        if (val === 'custom') {
            updatedDetails[index] = { ...current, selectedPriceId: 'custom', price: '', finalPrice: 0 };
        } else {
            const selected = (current.priceOptions || []).find(p => String(p.id) === String(val));
            updatedDetails[index] = {
                ...current,
                selectedPriceId: val,
                // guardo string para edición posterior
                price: selected?.price != null ? String(selected.price) : '',
                finalPrice: selected?.price != null ? Number(selected.price) : 0,
            };
        }

        const totalFormatted = sumDetailsTotal(updatedDetails);
        setInvoice(prev => ({ ...prev, invoice_detail: updatedDetails, total: totalFormatted }));
    };

    const handleRemoveProduct = (index) => {
        const removed = (invoice.invoice_detail || [])[index];

        const updatedDetails = (invoice.invoice_detail || []).filter((_, i) => i !== index);

        setProducts(prev =>
            (prev || []).map(p => (p.id === removed?.product_id ? { ...p, isAdd: false } : p))
        );

        const totalFormatted = sumDetailsTotal(updatedDetails);
        setInvoice(prev => ({ ...prev, invoice_detail: updatedDetails, total: totalFormatted }));
    };
    const handleAddPromotion = (index) => {
        setCurrentDetailIndex(index);
        setOpenPromotionModal(true);
    };
    const handleCleaningData = () => {
        setInvoice(prev => ({
            ...prev,
            invoice_detail: [],
            invoice_bonus: [],
            total: '0.00',
        }));

        // Opcional: también puedes resetear el estado de los productos si es necesario
        setProducts(products.map(product => ({
            ...product,
            isAdd: false
        })));

        // Si tienes un método para actualizar el total, deberías llamarlo
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    marginBottom: '10px',
                    gap: '10px'
                }}
            >
                {/* TextField para la búsqueda */}
                <TextField
                    label="Buscar"
                    variant="standard"
                    value={searchTerm}
                    size='small'
                    onChange={(e) => {
                        const v = e.target.value;
                        setSearchTerm(v);
                        if (typeof onSearchTermChange === 'function') onSearchTermChange(v);
                    }}
                    sx={{
                        flexGrow: 1, // Permite que el TextField ocupe el espacio disponible
                        // maxWidth: '300px', // Limita el ancho máximo del TextField si es necesario
                        marginBottom: '1px', // Pequeño margen inferior como pediste
                        marginLeft: '5px',
                        marginRight: '5px'
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center', // Alinea los botones entre sí verticalmente
                        gap: '10px',          // Espacio entre los dos botones
                        // Aquí puedes agregar un marginTop o paddingBottom si necesitas ajustar la altura
                        // de este grupo de botones respecto al TextField, pero 'alignItems: flex-end'
                        // en el padre suele ser suficiente.
                    }}
                >
                    <Button
                        sx={{
                            fontSize: '12px',
                            color: 'info',
                        }}
                        variant="outlined"
                        onClick={handleOpen}
                        startIcon={<AddShoppingCart />}
                    >
                        Agregar producto
                    </Button>
                    <CleanningButton
                        tooltipText='Borrar contenido'
                        onClick={handleCleaningData}
                        iconColor='primary'
                        paddingBottom='5px'

                    />
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 800 }} aria-label="invoice products table">
                    <TableHead sx={{ background: "#10173c" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Código</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Producto</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="right">Cantidad</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="right">Formato</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="center">Precio</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.map(({ row, index: sourceIndex }) => {
                            const rowId = getDetailRowId(row);
                            const detailMeta = detailMetaByIndex[sourceIndex];
                            const detailFlags = detailMeta?.flags || [];
                            const suggestedPrice = Number(detailMeta?.suggested_price ?? NaN);
                            const invoicePrice = Number(row?.price ?? row?.finalPrice ?? 0);
                            const unitImpact = Number.isFinite(suggestedPrice) ? invoicePrice - suggestedPrice : 0;
                            const totalImpact = unitImpact * (Number(row?.quantity) || 0);
                            return (
                            <TableRow
                                key={`${rowId}-${sourceIndex}`}
                            >
                                <TableCell align="center">
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            minWidth: '24px',
                                            px: 1,
                                            py: '2px',
                                            backgroundColor: '#EEF1F6',
                                            color: '#2B3E4D',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            fontFamily: 'Segoe UI, Roboto, sans-serif',
                                            textAlign: 'center',
                                            boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
                                        }}
                                    >
                                        {sourceIndex + 1}
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}>{row.code}</TableCell>
                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{row.description}</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
                                        {renderStatusChips(detailFlags, detailMeta)}
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                    {/* <TextField
                                        type="number"
                                        value={row.quantity || ''}
                                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                                        size="small"
                                        inputProps={{ min: 1 }}
                                        sx={{ width: '80px', fontSize: '0.85rem' }}
                                    /> */}
                                    <TextField
                                        type="text"                               // ← en vez de "number"
                                        value={row.quantity ?? ''}                // ← string controlada
                                        onChange={(e) => handleQuantityChange(sourceIndex, e.target.value)}
                                        size="small"
                                        inputProps={{ inputMode: 'numeric' }}     // teclado numérico móvil
                                        sx={{ width: '80px', fontSize: '0.85rem' }}
                                    />

                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                    <FormControl size="small" sx={{ width: 'auto' }}>
                                        <Select
                                            value={row.prformat_id ?? row.prformat?.id ?? ''}
                                            onChange={(e) => handleFormatChange(sourceIndex, e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value=''>—</MenuItem>
                                            {formats.map((formatItem) => (
                                                <MenuItem key={formatItem.id} value={formatItem.id}>
                                                    {formatItem.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                    {/* <FormControl size="small" sx={{ width: 'auto' }}>
                                        <Select
                                            value={row.selectedPriceId ?? ''}
                                            displayEmpty
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const updatedDetails = [...(invoice.invoice_detail || [])];
                                                let current = { ...updatedDetails[index] };
                                                if (val === 'custom') {
                                                    current = { ...current, selectedPriceId: 'custom', price: '' };
                                                } else {
                                                    const selected = current.priceOptions?.find(p => String(p.id) === String(val));
                                                    current = { ...current, selectedPriceId: val, price: selected?.price ? parseFloat(selected.price) : '' };
                                                }
                                                updatedDetails[index] = current;
                                                setInvoice(prev => ({ ...prev, invoice_detail: updatedDetails }));
                                                updateTotal();
                                            }}
                                        >
                                            {row.priceOptions?.map((p) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {`${p.price} (${getPriceTypeLabel(p.priceType)})`}
                                                </MenuItem>
                                            ))}
                                            <MenuItem value="custom">Ingresar precio</MenuItem>
                                        </Select>

                                    </FormControl>
                                    {String(row.selectedPriceId) === 'custom' && (
                                        <TextField
                                            type="text"  // ← permite borrar y reescribir fácilmente
                                            value={row.price === 0 ? '0.00' : row.price?.toString() || ''}
                                            onChange={(e) => handlePriceChange(index, e.target.value)}
                                            size="small"
                                            inputProps={{ inputMode: "decimal" }}  // mejora UX en móviles
                                            sx={{ width: '100px', ml: 1 }}
                                        />
                                    )} */}
                                    <FormControl size="small" sx={{ width: 'auto' }}>
                                        <Select
                                            value={String(row.selectedPriceId ?? '')}
                                            displayEmpty
                                            onChange={(e) => handleSelectPrice(sourceIndex, String(e.target.value))}
                                        >
                                            {row.priceOptions?.map((p) => (
                                                <MenuItem key={p.id} value={String(p.id)}>
                                                    {`${p.price} (${getPriceTypeLabel(p.priceType)})`}
                                                </MenuItem>
                                            ))}
                                            <MenuItem value="custom">Ingresar precio</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {String(row.selectedPriceId) === 'custom' && (
                                        <TextField
                                            type="text"
                                            value={row.price ?? ''}                      // ← string directa
                                            onChange={(e) => handlePriceChange(sourceIndex, e.target.value)}
                                            size="small"
                                            inputProps={{ inputMode: "decimal" }}
                                            sx={{ width: '100px', ml: 1 }}
                                        />
                                    )}
                                    {detailFlags.includes('PRECIO_DISTINTO') && Number.isFinite(suggestedPrice) && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.4 }}>
                                            OC: Q {suggestedPrice.toFixed(2)} | Du: Q {unitImpact.toFixed(2)} | Impacto: Q {totalImpact.toFixed(2)}
                                        </Typography>
                                    )}

                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.5 }}>
                                    <IconButton
                                        color="success"
                                        onClick={() => handleAddPromotion(sourceIndex)}
                                    >
                                        <Discount />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => handleRemoveProduct(sourceIndex)}>
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )})}

                        {invoice.invoice_bonus?.map((promo, promoIndex) => (
                            <TableRow
                                key={`promo-${promoIndex}`}
                                sx={{
                                    '& td': {
                                        py: '2px',
                                        color: 'success.main',
                                        fontSize: '0.85rem',
                                        borderBottom: '1px dashed rgba(0, 0, 0, 0.12)',
                                    },
                                }}
                            >
                                <TableCell align='center'>
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            minWidth: '24px',
                                            px: 1,
                                            py: '2px',
                                            backgroundColor: 'rgba(76, 175, 80, 0.3)', // Verde success con 30% de opacidad
                                            color: '#1b5e20', // Verde oscuro
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            fontFamily: 'Segoe UI, Roboto, sans-serif',
                                            textAlign: 'center',
                                            boxShadow: 'inset 0 0 0 1px rgba(27, 94, 32, 0.2)', // Borde sutil
                                        }}
                                    >
                                        {promoIndex + 1}
                                    </Box>
                                </TableCell>
                                <TableCell>{promo.code}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                        {promo.description} (Promocion)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
                                        {renderStatusChips(bonusMetaByIndex[promoIndex]?.flags || [], bonusMetaByIndex[promoIndex])}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        type="number"
                                        value={promo.quantity !== 0 ? promo.quantity : ''}
                                        onChange={(e) => handleBonusQuantityChange(promoIndex, e.target.value)}
                                        size="small"
                                        inputProps={{ min: 0 }}
                                        sx={{ width: '80px', fontSize: '0.85rem' }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Select
                                        value={promo.prformat_id ?? promo.prformat?.id ?? ''}
                                        onChange={(e) => handleBonusFormatChange(promoIndex, e.target.value)}
                                        displayEmpty
                                        size='small'
                                    >
                                        <MenuItem value=''>—</MenuItem>
                                        {formats.map((formatItem) => (
                                            <MenuItem key={formatItem.id} value={formatItem.id}>
                                                {formatItem.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                                <TableCell align="right">-</TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            setInvoice(prev => ({
                                                ...prev,
                                                invoice_bonus: prev.invoice_bonus.filter((_, i) => i !== promoIndex)
                                            }));
                                        }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}


                    </TableBody>
                </Table>
            </TableContainer>

            <Paper sx={{ mt: 1, p: 2, background: '#f5f5f5' }}>
                <Box display="flex" justifyContent="flex-end">
                    <Box sx={{ textAlign: 'right', mr: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            Total:
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format((Number(invoice.total || 0) || 0) + (Number(invoice.invoice_adjustment || 0) || 0))}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </>
    );
}
